import { useState } from "react";
import type { VideoType } from "@/types/video";
import { REGION_OPTIONS, YEAR_OPTIONS, GENRE_OPTIONS, SORT_OPTIONS, type FilterState, type SortKey } from "@/utils/video";

interface FilterBarProps {
  type: VideoType;
  filters: FilterState;
  onChange: (key: keyof FilterState, value: string) => void;
  onSort: (sort: SortKey) => void;
  onReset: () => void;
}

function PillRow({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-800 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <span className="w-16 flex-shrink-0 text-xs text-foreground-500 sm:text-sm">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors sm:text-sm ${
              value === option
                ? "bg-primary-500 font-medium text-background-950"
                : "bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ type, filters, onChange, onSort, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const body = (
    <div>
      <PillRow label="地区" options={REGION_OPTIONS} value={filters.region} onSelect={(v) => onChange("region", v)} />
      <PillRow label="年份" options={YEAR_OPTIONS} value={filters.year} onSelect={(v) => onChange("year", v)} />
      <PillRow label="类型" options={GENRE_OPTIONS[type]} value={filters.genre} onSelect={(v) => onChange("genre", v)} />

      <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:items-center sm:gap-4">
        <span className="w-16 flex-shrink-0 text-xs text-foreground-500 sm:text-sm">排序</span>
        <div className="flex flex-wrap items-center gap-2">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onSort(option.key)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors sm:text-sm ${
                filters.sort === option.key
                  ? "bg-accent-500 font-medium text-background-950"
                  : "bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100"
              }`}
            >
              <i className={option.key === "hot" ? "ri-fire-line" : option.key === "rating" ? "ri-star-line" : "ri-time-line"}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-background-800 bg-background-900 p-4 md:p-5">
      <div className="flex items-center justify-between md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium text-foreground-100"
        >
          <i className="ri-filter-3-line text-primary-400"></i>
          筛选与排序
          <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-400`}></i>
        </button>
        <button type="button" onClick={onReset} className="whitespace-nowrap text-xs text-foreground-500">
          重置
        </button>
      </div>

      <div className="hidden md:block">{body}</div>
      {open && <div className="mt-2 md:hidden">{body}</div>}

      <div className="mt-4 hidden items-center justify-end border-t border-background-800 pt-4 md:flex">
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-700 px-4 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}